function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return [h * 360, s * 100, l * 100];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val));
}

// Returns a palette of shades as "R G B" strings (Tailwind CSS var format).
export function generatePalette(hex: string): Record<string, string> {
  const [h, s, l] = hexToHsl(hex);

  const shades: Record<string, [number, number, number]> = {
    50:  hslToRgb(h, clamp(s * 0.12, 5, 100),  clamp(l + (100 - l) * 0.94, 90, 98)),
    100: hslToRgb(h, clamp(s * 0.20, 8, 100),  clamp(l + (100 - l) * 0.87, 85, 96)),
    200: hslToRgb(h, clamp(s * 0.35, 12, 100), clamp(l + (100 - l) * 0.74, 76, 93)),
    300: hslToRgb(h, clamp(s * 0.55, 20, 100), clamp(l + (100 - l) * 0.55, 62, 86)),
    400: hslToRgb(h, clamp(s * 0.78, 35, 100), clamp(l + (100 - l) * 0.28, 45, 74)),
    500: hslToRgb(h, s, l),
    600: hslToRgb(h, clamp(s * 1.05, 0, 100),  clamp(l * 0.82, 4, 95)),
    700: hslToRgb(h, clamp(s * 1.10, 0, 100),  clamp(l * 0.68, 3, 90)),
    900: hslToRgb(h, clamp(s * 1.20, 0, 100),  clamp(l * 0.40, 2, 70)),
  };

  return Object.fromEntries(
    Object.entries(shades).map(([shade, [r, g, b]]) => [shade, `${r} ${g} ${b}`]),
  );
}

export function applyTheme(primaryColor: string, secondaryColor: string) {
  const root = document.documentElement;
  const primary = generatePalette(primaryColor);
  const secondary = generatePalette(secondaryColor);

  Object.entries(primary).forEach(([shade, value]) => {
    root.style.setProperty(`--brand-${shade}`, value);
  });
  Object.entries(secondary).forEach(([shade, value]) => {
    root.style.setProperty(`--accent-${shade}`, value);
  });
}
